import React from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import HeroBreadcrumb, {
  BreadcrumbSegment,
} from "../../ui/organisms/HeroBreadcrumb"
import ShopProducts from "../../ui/organisms/Shop/ShopProducts"
import { setScrolling } from "../../utils/scrolling"
import FiltersModal from "./components/filters/FiltersModal"
import MobileFilters from "./components/header/MobileFilters"
import OrderByPanel from "./components/sorting/OrderByPanel"
import Loader from "../../ui/atoms/Loader"
import EditorialContents from "../editorialPage/components/EditorialContents"
import FiltersSidebar from "./components/filters/FiltersSidebar"
import OrderByDropdown from "./components/sorting/OrderByDropdown"
import Stroke from "../../ui/atoms/Stroke"
import { lightestGray } from "../../themes/variables"
import ToggleButton from "../../ui/molecules/ToggleButton"
import CollapsibleCol from "../../ui/molecules/CollapsibleCol"
import {
  FilterCategory,
  CheckboxFilterOption,
} from "./components/filters/types"
import FilterTags from "./components/filters/FilterTags"
import FiltersSidebarSkeleton from "./components/filters/FiltersSidebarSkeleton"
import ProductGridSkeleton from "../../ui/organisms/Product/ProductGridSkeleton"
import {
  calculateNewFilters,
  updateSearchFilters,
} from "./converters/searchParams"
import FilterChips from "./components/filters/FilterChips"
import { isBooleanFilterCategory } from "./utils/filters"
import DesktopOnly from "../../root/punks/mui/visibility/DesktopOnly"
import MobileOnly from "../../root/punks/mui/visibility/MobileOnly"
import classes from "./SerpContainer.module.css"
import {
  SearchControllerParams,
  SearchFilters,
} from "../../features/search/types"
import { useSearchResults } from "../../features/search/context"
import { convertToFilterCategories } from "./converters/filters"
import {
  CustomBox,
  CustomCol,
  CustomContainer,
  CustomRow,
} from "../../ui/spacings"
import Typography from "../../ui/atoms/Typography"
import { useRouter } from "next/router"
import MobileBreadcrumb from "./components/header/MobileBreadcrumb"
import { useLabels } from "../../features/contents/hooks"
import { trackSearch } from "../../features/tracking"
import { Collapse } from "../../ui/animations"
import { useLayoutData } from "../../aggregators/rootData"
import { headerShrinkSize } from "../../ui/organisms/HeaderMobile"
import BreadcrumbJsonLD from "../../features/seo/components/BreadcrumbJsonLD"
import { AvailabilitySearchFilters, AvailabilitySearchSorting } from "./types"

export interface SerpContainerProps {
  title: string
  subtitle?: React.ReactNode
  description?: React.ReactNode
  descriptionExpandable?: boolean
  intro?: React.ReactNode
  breadcrumb: BreadcrumbSegment[]
  contents: any
  initialFilters?: AvailabilitySearchFilters
  searchParams?: SearchControllerParams
  onFiltersChange: (newValue: AvailabilitySearchFilters) => void
  onSortingChange: (newValue: AvailabilitySearchSorting) => void
  onLoadNextResultsPage: () => void
  heroBackgroundImage?: string
  heroBackgroundImageMobile?: string
  hideHero?: boolean
  heroLogo?: React.ReactNode
  heroClassName?: string
}

const SerpContainer = ({
  contents,
  breadcrumb,
  title,
  subtitle,
  description,
  descriptionExpandable,
  intro,
  onLoadNextResultsPage,
  searchParams,
  onFiltersChange,
  onSortingChange,
  heroBackgroundImage,
  heroBackgroundImageMobile,
  hideHero,
  heroLogo,
  heroClassName,
}: SerpContainerProps) => {
  const {
    loading,
    ready,
    results,
    filters,
    initialFilters,
    isLastResultsPage,
    operationId,
    totResults,
  } = useSearchResults()
  const layout = useLayoutData()
  const [filtersModalOpen, setFiltersModalOpen] = React.useState(false)
  const [orderByExpanded, setOrderByExpanded] = React.useState(false)
  const [uiFilters, setUiFilters] = React.useState<FilterCategory[]>([])
  const [sidebarCollapse, setSidebarCollapse] = React.useState(false)

  const [initialized, setInitialized] = React.useState(true)
  const handleRouteChange = (newRoute: string) => {
    setFiltersModalOpen(false)
    if (
      newRoute.startsWith("/c/") ||
      newRoute.startsWith("/brand/") ||
      newRoute.startsWith("/promo/")
    ) {
      setInitialized(false)
    }
  }

  const { getLabel } = useLabels()
  const router = useRouter()

  React.useEffect(() => {
    trackSearch()
    router.events.on("routeChangeStart", handleRouteChange)
    return () => {
      router.events.off("routeChangeStart", handleRouteChange)
    }
  }, [])

  React.useEffect(() => {
    if (ready) {
      setInitialized(true)
    }
  }, [ready])

  React.useEffect(() => setScrolling(!orderByExpanded), [orderByExpanded])
  React.useEffect(() => {
    if (!searchParams || !filters) {
      return
    }

    setUiFilters(
      convertToFilterCategories(
        filters as SearchFilters,
        searchParams as SearchControllerParams,
        initialFilters
      )
    )
  }, [operationId, filters, loading])

  const handleSortingChange = (value: AvailabilitySearchSorting) => {
    setOrderByExpanded(false)
    onSortingChange(value)
  }

  const handleFiltersChange = (items: FilterCategory[]) => {
    if (!searchParams) {
      return
    }
    onFiltersChange(updateSearchFilters(searchParams, items))
  }

  const handleFilterOptionChange = (option: CheckboxFilterOption) => {
    const newFilters = calculateNewFilters(uiFilters, option)
    setUiFilters(newFilters)
    handleFiltersChange(newFilters)
  }

  const switchFilters = uiFilters.filter(isBooleanFilterCategory)
  const nonSwitchFilters = uiFilters.filter((x) => !isBooleanFilterCategory(x))

  return (
    <>
      {(breadcrumb?.length ?? 0) > 0 && (
        <BreadcrumbJsonLD segments={breadcrumb} />
      )}
      {hideHero !== true && (
        <>
          <DesktopOnly lg>
            <HeroBreadcrumb
              title={title}
              subtitle={subtitle}
              description={description}
              descriptionExpandable={descriptionExpandable}
              backgroundImage={heroBackgroundImage}
              backgroundImageMobile={heroBackgroundImageMobile}
              segments={breadcrumb}
              logo={heroLogo}
              center
              className={heroClassName}
            />
          </DesktopOnly>
          <MobileOnly lg>
            <MobileBreadcrumb
              title={title}
              subtitle={subtitle}
              logo={heroLogo}
              className={heroClassName}
              breadcrumb={[
                breadcrumb[0],
                breadcrumb[Math.max(1, breadcrumb.length - 2)],
              ].filter((x) => x)}
            />
          </MobileOnly>
        </>
      )}

      {intro}

      <EditorialContents contents={contents.data?.topContents} />

      <MobileOnly
        className="sticky z-10 bg-white"
        style={{
          top: layout.header.mobileTopPadding - headerShrinkSize,
          maxHeight: "calc(100vh + 45px)",
        }}
        lg
      >
        <MobileFilters
          orderByExpanded={orderByExpanded}
          onFiltersClick={() => setFiltersModalOpen(true)}
          onOrderByClick={() => setOrderByExpanded(!orderByExpanded)}
        />
        <Collapse in={orderByExpanded}>
          <div>
            <OrderByPanel
              current={searchParams?.sorting}
              onChange={handleSortingChange}
            />
          </div>
        </Collapse>
      </MobileOnly>

      {results !== undefined && (
        <>
          <DesktopOnly lg>
            <CustomContainer>
              <CustomBox py={3} flex="row" justifyContent="space-between">
                <div>
                  <ToggleButton
                    value={sidebarCollapse}
                    onChange={setSidebarCollapse}
                  >
                    {getLabel("search", "filters")}
                  </ToggleButton>
                </div>
                <div>
                  <CustomBox flex="row" alignItems="center">
                    <div className="mr-4">
                      <Typography variant="body1">
                        {getLabel("search", "order-by")}
                      </Typography>
                    </div>
                    <OrderByDropdown
                      current={searchParams?.sorting}
                      onChange={handleSortingChange}
                    />
                  </CustomBox>
                </div>
              </CustomBox>
            </CustomContainer>
            <Stroke colorValue={lightestGray} />
          </DesktopOnly>
          <div className="mb-8">
            <CustomContainer>
              <CustomRow>
                <CollapsibleCol
                  xs={12}
                  lg={3}
                  collapsed={sidebarCollapse}
                  className="sticky top-0 items-start"
                >
                  <DesktopOnly className="sticky top-0 pt-1 pr-8 mt-1" lg>
                    {uiFilters.length > 0 && initialized ? (
                      <FiltersSidebar
                        filters={nonSwitchFilters}
                        onFiltersUpdate={(filters) => {
                          setFiltersModalOpen(false)
                          setUiFilters([...filters, ...switchFilters])
                          handleFiltersChange([...filters, ...switchFilters])
                        }}
                      />
                    ) : (
                      <FiltersSidebarSkeleton />
                    )}
                  </DesktopOnly>
                </CollapsibleCol>
                <CustomCol xs={12} lg={sidebarCollapse ? 12 : 9}>
                  {results && initialized && (
                    <CustomBox
                      mt={3}
                      mb={2}
                      flex="row"
                      justifyContent="space-between"
                      className={classes.resultsHeadBar}
                    >
                      <div className={classes.resultsChips}>
                        <FilterChips
                          filters={switchFilters}
                          onOptionToggle={(option) =>
                            handleFilterOptionChange({
                              ...option,
                              selected: !option.selected,
                            })
                          }
                        />
                      </div>

                      <DesktopOnly>
                        <div style={{ minHeight: 16 }}>
                          {results.length > 0 && (
                            <Typography
                              variant="body1"
                              className={classes.totResults}
                            >
                              {totResults}{" "}
                              {totResults === 1
                                ? getLabel("search", "article")
                                : getLabel("search", "articles")}
                            </Typography>
                          )}
                        </div>
                      </DesktopOnly>
                    </CustomBox>
                  )}
                  <div className="sm:mb-4">
                    <FilterTags
                      filters={nonSwitchFilters}
                      onOptionRemove={(option) =>
                        handleFilterOptionChange({
                          ...option,
                          selected: false,
                        })
                      }
                    />
                  </div>
                  <MobileOnly className="my-4">
                    <div style={{ minHeight: 16 }}>
                      {results.length > 0 && (
                        <Typography
                          variant="body1"
                          className={classes.totResults}
                        >
                          {totResults}{" "}
                          {totResults === 1
                            ? getLabel("search", "article")
                            : getLabel("search", "articles")}
                        </Typography>
                      )}
                    </div>
                  </MobileOnly>
                  {ready && results.length === 0 && (
                    <div className="mt-6 mb-8">
                      <Typography>
                        {getLabel("search", "no-results")}
                      </Typography>
                    </div>
                  )}
                  {initialized && results.length > 0 && (
                    <div className="my-6">
                      <InfiniteScroll
                        dataLength={results.length}
                        hasMore={!isLastResultsPage}
                        next={onLoadNextResultsPage}
                        loader={<></>}
                        scrollableTarget="body"
                        style={{ overflow: "visible !important" }}
                      >
                        <ShopProducts
                          products={results}
                          colSizeXs={6}
                          colSizeMd={4}
                          colSizeLg={sidebarCollapse ? 2 : 3}
                          colSizeXl={sidebarCollapse ? 2 : 3}
                        />
                      </InfiniteScroll>
                    </div>
                  )}
                  {(!initialized || (!ready && results.length === 0)) && (
                    <div className="py-4">
                      <ProductGridSkeleton tot={16} colXs={6} colSm={3} />
                    </div>
                  )}
                </CustomCol>
              </CustomRow>
            </CustomContainer>
          </div>
        </>
      )}
      {!ready && results && results.length > 0 && (
        <div className="my-4 text-center">
          <Loader />
        </div>
      )}
      <EditorialContents contents={contents.data?.bottomContents} />
      <FiltersModal
        filters={nonSwitchFilters}
        onFiltersUpdate={(filters) =>
          setUiFilters([...filters, ...switchFilters])
        }
        open={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        onConfirm={(filters) => {
          handleFiltersChange([...filters, ...switchFilters])
          setFiltersModalOpen(false)
        }}
      />
    </>
  )
}

export default SerpContainer
